import { useCallback, useMemo, useState } from 'react';

import { App, DatePicker, Tooltip } from 'antd';
import dayjs from 'dayjs';
import {
  Layers,
  Satellite,
  Waves,
  ChevronDown,
  Eye,
  EyeOff,
  Droplets,
  Circle,
  Landmark,
  ArrowDownToLine,
  AlertTriangle,
  LayoutGrid,
  RotateCcw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { env } from '@/config/env';
import {
  GEE_ALPHAEARTH_KNOWN_AVAILABLE_YEARS,
  GEE_ALPHAEARTH_LATEST_KNOWN_AVAILABLE_YEAR,
} from '@/config/map';
import { useGetSensorTypesQuery } from '@/features/admin/api/opsManagementApi';
import {
  sensorTypeCatalogKey,
  sensorTypeCatalogKeyLower,
} from '@/features/admin/utils/sensorTypeCatalogKey';
import { getIotLayerKeyDotCssColor } from '@/features/map/utils/iotStationStatusColor';
import { useDashboardIoTStations } from '@/features/realtime/hooks/useDashboardIoTStations';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { shouldFetchOperationalApi } from '@/utils/dataSource';

import { resetLayersToDefault, setLayerVisible, setLayerOpacity } from '../stores/layerSlice';
import {
  resetLayerPanelMapPreferences,
  setGeeParams,
  setIoTStationTypeVisible,
  setIoTStationTypesVisibility,
  setShowFloodDepthAnalysis,
  setShowFloodExtentAnalysis,
  setShowIoTStationsHeatmap,
} from '../stores/mapSlice';
import type { AlphaEarthIndex, LayerId } from '../types/map.types';
import {
  IOT_STATION_LAYER_KEYS,
  IOT_VISIBILITY_UNKNOWN_KEY,
  iotStationVisibilityKey,
  iotStationVisibilityKeys,
} from '../types/map.types';

const ALPHA_EARTH_PICKER_YEAR_MIN = GEE_ALPHAEARTH_KNOWN_AVAILABLE_YEARS[0];
const ALPHA_EARTH_PICKER_YEAR_MAX = GEE_ALPHAEARTH_LATEST_KNOWN_AVAILABLE_YEAR;

/** Chế độ raster cho lớp GEE AlphaEarth. */
const GEE_LAYER_RASTER_OPTIONS: readonly AlphaEarthIndex[] = [
  'RGB',
  'NDWI',
  'MNDWI',
  'NDVI',
  'NDBI',
  'BAIS2',
];

const LAYER_ICONS: Partial<Record<LayerId, React.ReactNode>> = {
  gee: <Satellite size={14} color="var(--neon-cyan)" strokeWidth={2.5} />,
  flood: <Waves size={14} color="#0ea5e9" strokeWidth={2.5} />,
  pumps: <Droplets size={14} color="#eab308" strokeWidth={2.5} />,
  culverts: <Circle size={14} color="#7dd3fc" strokeWidth={2.5} />,
  canals: <Waves size={14} color="#38bdf8" strokeWidth={2.5} />,
  dykes: <Landmark size={14} color="#94a3b8" strokeWidth={2.5} />,
  lowlying: <ArrowDownToLine size={14} color="#2dd4bf" strokeWidth={2.5} />,
  landslides: <AlertTriangle size={14} color="#ef4444" strokeWidth={2.5} />,
};

export function LayerPanel() {
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const layers = useAppSelector((state) => state.layer.layers);
  const geeParams = useAppSelector((state) => state.map.geeParams);
  const scenarioMode = useAppSelector((state) => state.scenario.mode);
  const showIoTStationsHeatmap = useAppSelector((state) => state.map.showIoTStationsHeatmap);
  const showFloodExtentAnalysis = useAppSelector((state) => state.map.showFloodExtentAnalysis);
  const showFloodDepthAnalysis = useAppSelector((state) => state.map.showFloodDepthAnalysis);
  const activeMapTab = useAppSelector((state) => state.map.activeTab);
  const iotStationTypeVisibility = useAppSelector((state) => state.map.iotStationTypeVisibility);
  const selectedDate = useAppSelector((state) => state.scenario.selectedDate);
  const analysisLayers = layers.filter((l) => l.id === 'gee' || l.id === 'flood');
  const hazardLayers = layers.filter((l) =>
    ['pumps', 'culverts', 'canals', 'dykes', 'lowlying', 'landslides'].includes(l.id),
  );
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useTranslation();

  const handleResetLayerDefaults = useCallback(() => {
    dispatch(resetLayersToDefault());
    dispatch(resetLayerPanelMapPreferences());
    message.success(t('layerPanel.resetToDefaultsSuccess'));
  }, [dispatch, message, t]);

  const scenarioDerivedAlphaRef = useMemo(() => {
    const d = dayjs(selectedDate);
    return d.isValid() ? d.format('YYYY-MM-DD') : `${geeParams.year}-06-15`;
  }, [selectedDate, geeParams.year]);

  const operational = shouldFetchOperationalApi();
  const { data: sensorTypes = [], isLoading: sensorTypesLoading } = useGetSensorTypesQuery(
    undefined,
    { skip: !operational },
  );
  const { stations: liveIotStations = [] } = useDashboardIoTStations({
    skip: scenarioMode !== 'live' || !operational,
  });

  const iotVisibilityRows = useMemo(() => {
    const legacyLabels = () =>
      IOT_STATION_LAYER_KEYS.map((k) => ({
        visibilityKey: k,
        label: t(`layerPanel.iotTypes.${k}`),
        dotColor: undefined as string | undefined,
      }));

    if (!operational) return legacyLabels();

    if (sensorTypesLoading && sensorTypes.length === 0 && liveIotStations.length === 0) {
      return legacyLabels();
    }

    const apiRows = [...sensorTypes]
      .sort(
        (a, b) =>
          (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
          sensorTypeCatalogKeyLower(a).localeCompare(sensorTypeCatalogKeyLower(b), undefined, {
            sensitivity: 'base',
          }),
      )
      .map((st) => {
        const catalogKey = sensorTypeCatalogKeyLower(st);
        const visibilityKey = catalogKey;
        const c = st.color?.trim();
        const label = (st.name?.trim() || sensorTypeCatalogKey(st).trim()).trim() || visibilityKey;
        return {
          visibilityKey,
          label,
          dotColor: c && /^#/.test(c) ? c : undefined,
        };
      })
      .filter((r) => r.visibilityKey.length > 0);

    const known = new Set(apiRows.map((r) => r.visibilityKey));
    const adHoc = new Set<string>();
    for (const s of liveIotStations) {
      const keys = iotStationVisibilityKeys(s);
      const list = keys.length > 0 ? keys : [iotStationVisibilityKey(s)];
      for (const k of list) {
        if (k === IOT_VISIBILITY_UNKNOWN_KEY) continue;
        if (!known.has(k)) adHoc.add(k);
      }
    }
    const extras = [...adHoc]
      .sort((a, b) => a.localeCompare(b))
      .map((k) => ({
        visibilityKey: k,
        label: k,
        dotColor: undefined as string | undefined,
      }));

    const hasUnknown = liveIotStations.some(
      (s) => iotStationVisibilityKey(s) === IOT_VISIBILITY_UNKNOWN_KEY,
    );
    const unknownRow = hasUnknown
      ? [
          {
            visibilityKey: IOT_VISIBILITY_UNKNOWN_KEY,
            label: t('layerPanel.iotTypeUnknown'),
            dotColor: undefined as string | undefined,
          },
        ]
      : [];

    const combined = [...apiRows, ...extras, ...unknownRow];
    if (combined.length === 0) return legacyLabels();
    return combined;
  }, [operational, sensorTypes, sensorTypesLoading, liveIotStations, t]);

  const anyIotVisible = useMemo(
    () =>
      iotVisibilityRows?.some((row) => iotStationTypeVisibility[row.visibilityKey] !== false) ??
      false,
    [iotVisibilityRows, iotStationTypeVisibility],
  );

  const handleToggleAllIoT = () => {
    const keys = iotVisibilityRows?.map((r) => r.visibilityKey) ?? [];
    dispatch(setIoTStationTypesVisibility({ keys, visible: !anyIotVisible }));
  };

  return (
    <div
      style={{
        width: 240,
        background: 'var(--map-chrome-panel)',
        backdropFilter: 'blur(16px)',
        borderRadius: 16,
        border: '1px solid var(--glass-border)',
        padding: '12px',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          userSelect: 'none',
          padding: '2px 4px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              flex: 1,
              minWidth: 0,
            }}
            onClick={() => setCollapsed((v) => !v)}
          >
            <Layers
              size={13}
              color="var(--neon-cyan)"
              strokeWidth={2.5}
              style={{ flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--text-primary)',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('layerPanel.title')}
            </span>
          </div>
          <button
            type="button"
            aria-expanded={!collapsed}
            aria-label={collapsed ? t('layerPanel.expandPanel') : t('layerPanel.collapsePanel')}
            onClick={() => setCollapsed((v) => !v)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-secondary)',
              flexShrink: 0,
            }}
          >
            <ChevronDown
              size={14}
              color="currentColor"
              style={{
                transition: 'transform 0.3s ease',
                transform: collapsed ? 'rotate(-90deg)' : 'none',
              }}
            />
          </button>
        </div>

        <Tooltip title={t('layerPanel.resetToDefaultsTooltip')}>
          <button
            type="button"
            aria-label={t('layerPanel.resetToDefaultsAria')}
            onClick={(e) => {
              e.stopPropagation();
              handleResetLayerDefaults();
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              width: '100%',
              boxSizing: 'border-box',
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid var(--glass-border)',
              background: 'var(--mdm-header-tint)',
              color: 'var(--text-secondary)',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.3px',
              cursor: 'pointer',
              transition: 'color 0.2s, border-color 0.2s, background 0.2s',
            }}
          >
            <RotateCcw size={12} strokeWidth={2.5} aria-hidden />
            <span style={{ whiteSpace: 'nowrap' }}>{t('layerPanel.resetToDefaults')}</span>
          </button>
        </Tooltip>
      </div>

      {!collapsed && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            maxHeight: '50vh',
            overflowY: 'auto',
            paddingRight: '4px',
          }}
          className="custom-scrollbar"
        >
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
                padding: '0 4px',
              }}
            >
              <div
                style={{ width: 2, height: 10, background: 'var(--neon-cyan)', borderRadius: 1 }}
              />
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                }}
              >
                {t('layerPanel.analysisData')}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {analysisLayers.map((layer) => (
                <div
                  key={layer.id}
                  style={{
                    background: 'var(--mdm-header-tint)',
                    borderRadius: 8,
                    padding: '8px',
                    border: '1px solid var(--glass-border)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: layer.visible ? 8 : 0,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {LAYER_ICONS[layer.id as LayerId]}
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: layer.visible ? 'var(--text-primary)' : 'var(--text-muted)',
                        }}
                      >
                        {t(`layerPanel.layers.${layer.id}`)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        dispatch(
                          setLayerVisible({ id: layer.id as LayerId, visible: !layer.visible }),
                        )
                      }
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: layer.visible ? 'var(--neon-cyan)' : 'var(--text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                      }}
                    >
                      {layer.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                  </div>

                  {layer.visible && (
                    <div style={{ paddingLeft: 4 }}>
                      {layer.id === 'gee' && (
                        <div style={{ marginBottom: 10 }}>
                          <div
                            style={{
                              fontSize: 9,
                              color: 'var(--text-muted)',
                              marginBottom: 6,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                            }}
                          >
                            {t('layerPanel.alphaEarthReferenceDate')}
                          </div>
                          <DatePicker
                            size="small"
                            allowClear
                            format="DD/MM/YYYY"
                            style={{ width: '100%', marginBottom: 8 }}
                            placeholder={t('layerPanel.alphaEarthReferencePlaceholder', {
                              date: scenarioDerivedAlphaRef,
                            })}
                            value={
                              geeParams.alphaEarthReferenceDate
                                ? dayjs(geeParams.alphaEarthReferenceDate)
                                : undefined
                            }
                            onChange={(d) =>
                              dispatch(
                                setGeeParams({
                                  alphaEarthReferenceDate: d ? d.format('YYYY-MM-DD') : null,
                                }),
                              )
                            }
                            disabledDate={(current) => {
                              if (!current) return false;
                              const y = current.year();
                              return (
                                y < ALPHA_EARTH_PICKER_YEAR_MIN || y > ALPHA_EARTH_PICKER_YEAR_MAX
                              );
                            }}
                          />
                          <div
                            style={{
                              fontSize: 8,
                              color: 'var(--text-muted)',
                              marginBottom: 10,
                              lineHeight: 1.35,
                            }}
                          >
                            {t('layerPanel.alphaEarthReferenceDateHint', {
                              min: ALPHA_EARTH_PICKER_YEAR_MIN,
                              max: ALPHA_EARTH_PICKER_YEAR_MAX,
                            })}
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: 'var(--text-muted)',
                              marginBottom: 6,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                            }}
                          >
                            {t('layerPanel.geeRasterMode')}
                          </div>
                          <select
                            value={geeParams.index}
                            title={t('layerPanel.geeRasterMode')}
                            onChange={(e) =>
                              dispatch(setGeeParams({ index: e.target.value as AlphaEarthIndex }))
                            }
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              borderRadius: 6,
                              border: '1px solid var(--glass-border)',
                              background: 'var(--form-field-bg)',
                              color: 'var(--text-primary)',
                              fontSize: 10,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {GEE_LAYER_RASTER_OPTIONS.map((idx) => (
                              <option
                                key={idx}
                                value={idx}
                                style={{
                                  background: 'var(--layer-chip-bg)',
                                  color: 'var(--text-primary)',
                                }}
                              >
                                {t(`dashboard.geeIndexes.${idx}`)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.01}
                          value={layer.opacity}
                          onChange={(e) =>
                            dispatch(
                              setLayerOpacity({
                                id: layer.id as LayerId,
                                opacity: Number(e.target.value),
                              }),
                            )
                          }
                          style={{
                            flex: 1,
                            height: 3,
                            borderRadius: 2,
                            appearance: 'none',
                            background: 'var(--slider-rail-bg)',
                            outline: 'none',
                            cursor: 'pointer',
                          }}
                          className="custom-range-slider"
                        />
                        <span
                          style={{
                            fontSize: 9,
                            color: 'var(--text-muted)',
                            fontWeight: 700,
                            minWidth: 24,
                            textAlign: 'right',
                          }}
                        >
                          {Math.round(layer.opacity * 100)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {env.SHOW_MAP_ANALYSIS_FILTERS && (
                <>
                  <div
                    style={{
                      background: 'var(--mdm-header-tint)',
                      borderRadius: 8,
                      padding: '8px',
                      border: '1px solid var(--glass-border)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}
                    >
                      <div
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}
                      >
                        <Waves
                          size={14}
                          color="#38bdf8"
                          strokeWidth={2.5}
                          style={{ flexShrink: 0, marginTop: 2 }}
                          aria-hidden
                        />
                        <div style={{ minWidth: 0 }}>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: showFloodExtentAnalysis
                                ? 'var(--text-primary)'
                                : 'var(--text-muted)',
                              display: 'block',
                              lineHeight: 1.25,
                            }}
                          >
                            {t('filters.floodExtentTitle')}
                          </span>
                          <span
                            style={{
                              fontSize: 9,
                              color: 'var(--text-muted)',
                              display: 'block',
                              marginTop: 3,
                              lineHeight: 1.35,
                            }}
                          >
                            {t('filters.floodExtentSubtitle')}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        aria-label={t('filters.floodExtentTitle')}
                        aria-pressed={showFloodExtentAnalysis}
                        onClick={() =>
                          dispatch(setShowFloodExtentAnalysis(!showFloodExtentAnalysis))
                        }
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: showFloodExtentAnalysis ? 'var(--neon-cyan)' : 'var(--text-muted)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                          flexShrink: 0,
                          padding: 2,
                        }}
                      >
                        {showFloodExtentAnalysis ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      background: 'var(--mdm-header-tint)',
                      borderRadius: 8,
                      padding: '8px',
                      border: '1px solid var(--glass-border)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}
                    >
                      <div
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}
                      >
                        <Waves
                          size={14}
                          color="#2dd4bf"
                          strokeWidth={2.5}
                          style={{ flexShrink: 0, marginTop: 2 }}
                          aria-hidden
                        />
                        <div style={{ minWidth: 0 }}>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: showFloodDepthAnalysis
                                ? 'var(--text-primary)'
                                : 'var(--text-muted)',
                              display: 'block',
                              lineHeight: 1.25,
                            }}
                          >
                            {t('filters.floodDepthTitle')}
                          </span>
                          <span
                            style={{
                              fontSize: 9,
                              color: 'var(--text-muted)',
                              display: 'block',
                              marginTop: 3,
                              lineHeight: 1.35,
                            }}
                          >
                            {t('filters.floodDepthSubtitle')}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        aria-label={t('filters.floodDepthTitle')}
                        aria-pressed={showFloodDepthAnalysis}
                        onClick={() => dispatch(setShowFloodDepthAnalysis(!showFloodDepthAnalysis))}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: showFloodDepthAnalysis ? 'var(--neon-cyan)' : 'var(--text-muted)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                          flexShrink: 0,
                          padding: 2,
                        }}
                      >
                        {showFloodDepthAnalysis ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {scenarioMode === 'live' && (
            <div style={{ borderTop: '1px solid var(--glass-border)', paddingTop: 10 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                  padding: '0 4px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <div style={{ width: 2, height: 10, background: '#22c55e', borderRadius: 1 }} />
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {t('layerPanel.iotSection')}
                  </span>
                </div>
                <Tooltip title={anyIotVisible ? t('layerPanel.hideAll') : t('layerPanel.showAll')}>
                  <button
                    type="button"
                    onClick={handleToggleAllIoT}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: anyIotVisible ? 'var(--neon-cyan)' : 'var(--text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      padding: '2px',
                    }}
                  >
                    {anyIotVisible ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                </Tooltip>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {iotVisibilityRows.map(({ visibilityKey: key, label, dotColor }) => {
                  const on = iotStationTypeVisibility[key] !== false;
                  return (
                    <div
                      key={key}
                      style={{
                        background: 'var(--mdm-header-tint)',
                        borderRadius: 8,
                        padding: '8px',
                        border: '1px solid var(--glass-border)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontSize: 11,
                            fontWeight: 700,
                            color: on ? 'var(--text-primary)' : 'var(--text-muted)',
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 999,
                              flexShrink: 0,
                              background: dotColor ?? getIotLayerKeyDotCssColor(key),
                              border: '1px solid rgba(148,163,184,0.35)',
                            }}
                          />
                          {label}
                        </span>
                        <button
                          type="button"
                          onClick={() => dispatch(setIoTStationTypeVisible({ key, visible: !on }))}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: on ? 'var(--neon-cyan)' : 'var(--text-muted)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s',
                          }}
                        >
                          {on ? <Eye size={14} /> : <EyeOff size={14} />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  marginTop: 6,
                  background: 'var(--mdm-header-tint)',
                  borderRadius: 8,
                  padding: '8px',
                  border: '1px solid var(--glass-border)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <LayoutGrid size={14} color="#22c55e" strokeWidth={2.5} />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: showIoTStationsHeatmap ? 'var(--text-primary)' : 'var(--text-muted)',
                      }}
                    >
                      {t('layerPanel.iotHeatmap')}
                    </span>
                    {activeMapTab === '3d' && (
                      <span
                        title={t('layerPanel.iotHeatmap3dUnavailable')}
                        style={{
                          fontSize: 8,
                          fontWeight: 600,
                          color: 'var(--text-muted)',
                          background: 'var(--glass-border)',
                          borderRadius: 4,
                          padding: '1px 4px',
                          marginLeft: 4,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                        }}
                      >
                        2D
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={activeMapTab === '3d'}
                    onClick={() => dispatch(setShowIoTStationsHeatmap(!showIoTStationsHeatmap))}
                    title={activeMapTab === '3d' ? t('layerPanel.iotHeatmap3dUnavailable') : undefined}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: activeMapTab === '3d' ? 'not-allowed' : 'pointer',
                      color:
                        activeMapTab === '3d'
                          ? 'var(--text-muted)'
                          : showIoTStationsHeatmap
                            ? 'var(--neon-cyan)'
                            : 'var(--text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                    }}
                  >
                    {showIoTStationsHeatmap ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ borderTop: '1px solid var(--glass-border)', paddingTop: 10 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
                padding: '0 4px',
              }}
            >
              <div style={{ width: 2, height: 10, background: '#f87171', borderRadius: 1 }} />
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                }}
              >
                {t('layerPanel.hazardInfrastructure')}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {hazardLayers.map((layer) => (
                <div
                  key={layer.id}
                  style={{
                    background: 'var(--mdm-header-tint)',
                    borderRadius: 8,
                    padding: '8px',
                    border: '1px solid var(--glass-border)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {LAYER_ICONS[layer.id as LayerId]}
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: layer.visible ? 'var(--text-primary)' : 'var(--text-muted)',
                        }}
                      >
                        {t(`layerPanel.layers.${layer.id}`)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        dispatch(
                          setLayerVisible({ id: layer.id as LayerId, visible: !layer.visible }),
                        )
                      }
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: layer.visible ? 'var(--neon-cyan)' : 'var(--text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                      }}
                    >
                      {layer.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
